import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { DocumentsStore } from '@qtek/libs/store';
import { WebSocketService, WSEntityManager } from '@qtek/core/websockets-core';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { DocumentType } from '@qtek/shared/models';
import { BehaviorSubject, filter, race, take, timer } from 'rxjs';
import {
  applyCcaExtraction,
  CcaExtractionResult,
} from './cca-extraction.utils';

export const closedDealEntryMethodDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'closed-deal-entry-method-dialog'],
  width: '600px',
  maxWidth: '95vw',
};

const CCA_DOC_TYPE = '550';
const CCA_EXTRACTION_TIMER = 180000; // 180s — CAs are the largest docs; covers the BE per-attempt Bedrock timeout (BEDROCK_CALL_TIMEOUT=180s) + queue slack. Term sheets use 120s (current-deal-entry-method-dialog); CA was left at 60s and lost the race on big docs (SD-2185).

@Component({
  selector: 'qt-closed-deal-entry-method-dialog',
  templateUrl: './closed-deal-entry-method-dialog.component.html',
  styleUrls: ['./closed-deal-entry-method-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
})
export class ClosedDealEntryMethodDialogComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly documentsStore = inject(DocumentsStore);
  private dialogRef = inject(
    MatDialogRef<ClosedDealEntryMethodDialogComponent>
  );
  private dialogLoader = inject(LazyDialogLoader);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private wsService = inject(WebSocketService);
  private docMetaWsManager = new WSEntityManager(this.wsService, 'docmeta');

  /** 'select' = card selection, 'upload' = file drop zone, 'processing' = extraction in progress */
  phase = signal<'select' | 'upload' | 'processing'>('select');
  dragCounter = 0;
  fileList: File[] = [];
  isUploading$ = new BehaviorSubject(false);
  currentUploadMessage$ = new BehaviorSubject('Uploading document...');
  uploadError = signal(false);

  private createdTemplateId: string | null = null;
  private ccaMysid: string | null = null;
  private uploadMessageTimer: any;

  private uploadMessages = [
    'Uploading document...',
    'Document uploaded successfully',
    'Processing credit agreement...',
    'Extracting deal fields...',
  ];

  // ─── Card Actions ─────────────────────────────────────

  selectManualEntry(): void {
    this.dialogRef.close();
    this.dialogLoader.open('closed-deal-stepper').subscribe();
  }

  selectUploadCA(): void {
    this.phase.set('upload');
  }

  goBack(): void {
    const currentPhase = this.phase();
    if (currentPhase === 'upload') {
      this.cleanupUploadState();
      this.phase.set('select');
    } else if (currentPhase === 'select') {
      this.dialogRef.close();
      this.dialogLoader.open('select-deal-type').subscribe();
    }
  }

  // ─── File Upload ──────────────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(input.files);
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  handleDrop(event: DragEvent): void {
    this.dragCounter = 0;
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(files);
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
  }

  // ─── Upload & Extraction ──────────────────────────────

  private startUploadProcess(): void {
    if (this.fileList.length === 0) return;
    this.phase.set('processing');
    this.startUploadMessageCycling();
    this.uploadCcaDocument();
  }

  private uploadCcaDocument(): void {
    this.isUploading$.next(true);
    this.uploadError.set(false);

    const formData = new FormData();
    formData.append('data', this.fileList[0]);
    formData.append('_ks', 'ndaTemplate={{<<crud-insert>>}}');
    formData.append('doc.dtp', CCA_DOC_TYPE);
    formData.append('_acc', '200');
    formData.append('_dsc', this.fileList[0].name);

    this.documentsStore
      .uploadDocuments({ data: formData, attr: DocumentType.CORPORATE_NDA })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: docs => {
          const docResponse = docs?.[0];
          if (docResponse?.refId) {
            const refIdValue = docResponse.refId.toString();
            const matches = refIdValue.match(/ndaTemplate=([^&]+)/);
            if (matches?.[1]) {
              this.createdTemplateId = matches[1];
            }
          }
          // Subscribe to DocMeta immediately on upload success to avoid race condition
          // with the Kafka worker extraction completing before the subscription is active
          if (docResponse?._id) {
            this.subscribeToDocMeta(docResponse._id);
          }
        },
        error: () => {
          this.uploadError.set(true);
          this.stopUploadMessageCycling();
          this.isUploading$.next(false);
          this.phase.set('upload');
        },
      });
  }

  private subscribeToDocMeta(docId: string): void {
    this.ccaMysid = `cca_${docId}_${Date.now()}`;
    // Subscribe directly via WebSocket — NdaEffects/store not available
    // in this standalone dialog context (nda feature state not loaded)
    const docMetaResponse$ = this.docMetaWsManager
      .subscribeEntity(
        {
          mysid: this.ccaMysid,
          q: [{ k: 'docId', op: 'EQ', v: [docId] }],
          s: 'model',
          view: 'pln',
        },
        true
      )
      .pipe(
        filter((msg: any) => msg.op === 'query' || msg.op === 'ins'),
        filter((msg: any) => !!msg.res),
        take(1)
      );

    const timeout$ = timer(CCA_EXTRACTION_TIMER);

    race(docMetaResponse$, timeout$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.stopUploadMessageCycling();
        this.isUploading$.next(false);

        if (value && typeof value === 'object' && (value as any).res) {
          this.handleExtractionSuccess((value as any).res, docId);
        } else {
          this.openStepperWithData(null, docId);
        }
      });
  }

  private handleExtractionSuccess(response: any, docId: string): void {
    const containers = response?.containers;
    if (!containers?.length || !containers[0]?.data) {
      this.openStepperWithData(null, docId);
      return;
    }

    // containers[0] = parent deal, containers[1..N] = sub-facilities
    const additionalContainers =
      containers.length > 1
        ? containers
            .slice(1)
            .map((c: any) => c.data)
            .filter(Boolean)
        : undefined;

    const result = applyCcaExtraction(containers[0].data, additionalContainers);
    this.openStepperWithData(result, docId);
  }

  private openStepperWithData(
    extractionResult: CcaExtractionResult | null,
    docId: string
  ): void {
    this.dialogRef.close();
    this.dialogLoader
      // SD-2150: pass the uploaded docId so the stepper links it to the new
      // deal (data-room visibility + covenant extraction discoverability).
      .open('closed-deal-stepper', { extractionResult, docId })
      .subscribe();
  }

  // ─── Message Cycling ──────────────────────────────────

  private startUploadMessageCycling(): void {
    this.currentUploadMessage$.next(this.uploadMessages[0]);
    let currentIndex = 0;
    const interval = CCA_EXTRACTION_TIMER / this.uploadMessages.length;

    this.uploadMessageTimer = setInterval(() => {
      currentIndex++;
      if (currentIndex < this.uploadMessages.length) {
        this.currentUploadMessage$.next(this.uploadMessages[currentIndex]);
      } else {
        this.stopUploadMessageCycling();
      }
    }, interval);
  }

  private stopUploadMessageCycling(): void {
    if (this.uploadMessageTimer) {
      clearInterval(this.uploadMessageTimer);
      this.uploadMessageTimer = null;
    }
  }

  // ─── Cleanup ──────────────────────────────────────────

  private cleanupUploadState(): void {
    this.stopUploadMessageCycling();
    this.fileList = [];
    this.createdTemplateId = null;
    this.isUploading$.next(false);
    this.uploadError.set(false);

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    this.ccaMysid = null;
  }

  ngOnDestroy(): void {
    this.cleanupUploadState();
    this.isUploading$.complete();
    this.currentUploadMessage$.complete();
  }
}
